import {
    Component,
    Watch,
    h,
    Host,
    Event,
    Element,
    EventEmitter,
    Prop,
} from "@stencil/core";
import { getUniqueId } from "../../utils/unique-id";
import { UxOption } from "./option";

@Component({
    tag: "ux-select",
    styleUrl: "select.scss",
    shadow: false,
})
export class Select {
    @Element() selectElement: HTMLUxSelectElement;

    /** array of options */
    @Prop() options: UxOption[] = [];
    /** label text describing the select component field */
    @Prop() label: string = "";
    /** additional info label for the the input field */
    @Prop() labelInfo: string = "";
    /** select id attribute */
    @Prop({ reflect: true, mutable: true }) selectId: string = "";
    /** name of the select field */
    @Prop() name: string = "";
    /** indicates if the select component is disabled */
    @Prop() disabled: boolean = false;
    /** indicates if the select component is required  */
    @Prop() required: boolean = false;
    /** indicates if the select component has an error state  */
    @Prop() error: boolean = false;
    /** Error message that is rendered if component has an error */
    @Prop() errorMsg: string = "";
    /** autocomplete attribute, will be set on the select element. Refer to HTML standards for allowed values */
    @Prop() autocomplete: string = null;
    /** will make the select element extend for the full width of the parent element */
    @Prop() fullWidth: boolean = false;

    @Watch("selectId")
    setSelectId() {
        this.selectId = this.selectId || getUniqueId();
    }

    componentWillLoad() {
        this.setSelectId();
        const firstOption = this.selectElement.querySelector("option");
        if (!firstOption) return;
        const optionSelected = this.selectElement.querySelector(
            "option[selected]"
        );
        if (!optionSelected) firstOption.setAttribute("selected", "");
    }

    /** event to set the currently active option */
    @Event({
        eventName: "uxSelectChange",
        composed: true,
        cancelable: true,
        bubbles: true,
    })
    changeEventDetail: EventEmitter;

    /** function to emit new active option value */
    private selectChange(newValue: any) {
        this.changeEventDetail.emit(newValue.value);
    }

    render() {
        const {
            label,
            labelInfo,
            selectId,
            name,
            disabled,
            required,
            error,
            errorMsg,
            fullWidth,
        } = this;

        return (
            <Host
                class={{
                    ["ux-select__wrapper"]: true,
                    ["ux-select__wrapper--full-width"]: fullWidth,
                }}
            >
                <ux-input-label
                    text={label}
                    labelClasses="ux-select__wrapper__label"
                    inputId={selectId}
                    error={error}
                    errorMsg={errorMsg}
                    required={required}
                    labelInfo={labelInfo}
                />
                <div
                    class={{
                        [`ux-select`]: true,
                        [`ux-select--disabled`]: disabled,
                        [`ux-select--error`]: error,
                    }}
                >
                    <ux-icon
                        class="ux-select__icon"
                        name="chevron-down"
                        weight="bold"
                    ></ux-icon>
                    <select
                        class="ux-select__select"
                        onChange={(e) => this.selectChange(e.target)}
                        id={selectId}
                        name={name}
                        disabled={disabled}
                        required={required}
                        aria-invalid={error ? "true" : "false"}
                        autocomplete={this.autocomplete}
                    >
                        {this.options.map((option) => (
                            <option
                                selected={option.selected}
                                value={option.value}
                            >
                                {option.name}
                            </option>
                        ))}
                        <slot />
                    </select>
                </div>
            </Host>
        );
    }
}
